import { z } from 'zod';

import { CustomQuestionAnswerBookingResponseSchema } from './custom-question-answer';
import { BookingCustomQuestionSchema, BookingCustomQuestionResponseSchema } from './custom-questions';
import { BaseParticipantSchema, ParticipantSchema } from './participants';
import { BookingDayPriceTierSchema, BookingPriceTierSchema } from './price-tiers';
import { BooleanStringSchema, DateStringSchema, EmailStringSchema, LocalDateTimeStringSchema } from '../../common';
import { buildQueryString } from '../../utils/querystring';
import { PaymentDetailSchema } from '../booking/payment-detail';
import { ProductLine } from '../brand/brand';
import { makeEndpoint, paginateQuerySchema, paginateResponseSchema } from '../common';
import { ExperienceSettingsSchema } from '../experience/experience-settings';
import { HostCommissionType } from '../experience/hosts';
import { LocationSchema } from '../location/location';
import { IMediaReferenceSchema } from '../media/media';
import { PaymentMethods } from '../payment-method/public';
export const BOOKING_SURCHARGE_AMOUNT_USD = 0.3;

// Claim token expiration duration for non-persistent tokens (in minutes)
export const CLAIM_TOKEN_EXPIRATION_MINUTES = 5;

export enum BookingMode {
  PRIVATE = 'private',
  SHARED = 'shared',
}

export enum DeviceType {
  PC = 'pc',
  MOBILE = 'mobile',
}

export const RefundHistorySchema = z.object({
  amount: z.coerce.number(),
  refundedAt: z.string().datetime(),
  refundedBy: z.string(),
});

export type RefundHistory = z.infer<typeof RefundHistorySchema>;

const BookingListingAddOnItemSchema = z.object({
  listingAddOnId: z.string().uuid(),
  quantity: z.number().positive(),
});

export type BookingListingAddOnItem = z.output<typeof BookingListingAddOnItemSchema>;

export const DetailedBookingAddOnItemSchema = z.object({
  id: z.string().uuid(),
  listingAddOnId: z.string().uuid(),
  label: z.string(),
  paidAmount: z.number(),
  price: z.number(),
  discountedAmount: z.number(),
  refundedAmount: z.number(),
  refundHistories: z.array(RefundHistorySchema),
  cancelledAt: z.string().datetime().nullable(),
});

export type DetailedBookingAddOnItem = z.output<typeof DetailedBookingAddOnItemSchema>;

export const MinimalBookingRequestSchema = z.object({
  sessionTime: LocalDateTimeStringSchema,
  sessionDuration: z.number().optional(),
  participants: z.array(ParticipantSchema),
  mode: z.nativeEnum(BookingMode),
  couponCode: z
    .string()
    .nullable()
    .optional()
    .transform((couponCode) => couponCode ?? null),
  discountAmount: z
    .number()
    .nullable()
    .optional()
    .transform((discountAmount) => discountAmount ?? null),
  discountPercentage: z
    .number()
    .nullable()
    .optional()
    .transform((discountPercentage) => discountPercentage ?? null),
  accessCode: z
    .string()
    .nullable()
    .optional()
    .transform((accessCode) => accessCode ?? null),
  notes: z
    .string()
    .optional()
    .nullable()
    .transform((notes) => notes ?? null),
  metadata: z
    .record(z.unknown())
    .nullable()
    .optional()
    .transform((metadata) => metadata ?? null),
  internalNotes: z
    .string()
    .optional()
    .nullable()
    .transform((internalNotes) => internalNotes ?? null),
  listingAddOns: z.array(BookingListingAddOnItemSchema).default([]),
  appliedCreditCodes: z
    .array(
      z.object({
        code: z.string(),
        appliedValue: z.number(),
        sequence: z.number(),
      }),
    )
    .nullable()
    .optional(),
  intendedConfirmationCode: z.string().optional(),
});

export const CommonBookingRequestSchema = MinimalBookingRequestSchema.extend({
  waitlistEntryId: z.string().uuid().optional(),
});

export const BookingRequestSchema = CommonBookingRequestSchema.extend({
  purchaser: z.object({
    firstName: z.string(),
    lastName: z.string(),
    emailAddress: EmailStringSchema,
    phoneNumber: z.string().optional(),
    customQuestionResponses: z.array(BookingCustomQuestionSchema).optional(),
  }),
  paymentMethod: z
    .nativeEnum(PaymentMethods)
    .optional()
    .transform((paymentMethod) => paymentMethod || null),
  paymentDetail: PaymentDetailSchema.nullable()
    .optional()
    .transform((paymentDetail) => paymentDetail || null),
  bookingScriptUrl: z.string().url().optional(),
  externalAnalyticsUserId: z.string().optional(),
  siteLanguage: z.string().optional(),
  waitlistEntryId: z.string().uuid().optional(),
  deviceType: z.nativeEnum(DeviceType).optional(),
  cartId: z.string().uuid().optional(),
});

export const BulkBookingExperienceSchema = CommonBookingRequestSchema.extend({
  experienceId: z.string(),
  additionalCustomQuestionResponses: z.array(BookingCustomQuestionResponseSchema).optional(),
  externalAnalyticsUserId: z.string().optional(),
  recommendationId: z.string().uuid().optional().nullable(),
  overrideSessionAddOnLimits: z.boolean().optional(),
});
export type BulkBookingExperience = z.infer<typeof BulkBookingExperienceSchema>;

export const loyaltyProgramDataSchema = z.object({
  loyaltyProgramId: z.string(),
  memberId: z.string(),
});

export type LoyaltyProgramData = z.infer<typeof loyaltyProgramDataSchema>;

export const BulkBookingRequestSchema = z.object({
  data: z.array(
    CommonBookingRequestSchema.extend({
      experienceId: z.string(),
      resourceGroupId: z.string().optional(),
      resourceQuantity: z.number().optional(),
      additionalCustomQuestionResponses: z.array(BookingCustomQuestionResponseSchema).optional(),
      recommendationId: z.string().uuid().optional().nullable(),
      overrideSessionAddOnLimits: z.boolean().optional(),
    }),
  ),
  purchaser: z.object({
    firstName: z.string(),
    lastName: z.string(),
    emailAddress: EmailStringSchema,
    phoneNumber: z.string().optional(),
    customQuestionResponses: z.array(BookingCustomQuestionSchema).optional(),
  }),
  loyaltyProgramData: loyaltyProgramDataSchema.optional(),
  paymentMethod: z
    .nativeEnum(PaymentMethods)
    .optional()
    .transform((paymentMethod) => paymentMethod || null),
  paymentDetail: PaymentDetailSchema.nullable()
    .optional()
    .transform((paymentDetail) => paymentDetail || null),
  bookingScriptUrl: z.string().url().optional(),
  externalAnalyticsUserId: z.string().optional(),
  siteLanguage: z.string().optional(),
  deviceType: z.nativeEnum(DeviceType).optional(),
  cartId: z.string().uuid().optional(),
  sendEmail: z.boolean().optional(),
  recommendationId: z.string().uuid().optional().nullable(),
});

export enum BookingStatus {
  CONFIRMED = 'confirmed',
  REFUNDED = 'refunded',
  PROCESSING = 'processing',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export enum BookingHistoryStatus {
  CONFIRMED = 'confirmed',
  PROCESSING = 'processing',
  FAILED = 'failed',
}

export enum ItemType {
  PRICE_TIER = 'Price tier',
  ADD_ON = 'Add-on',
}

export const BookingStatusesLeadingToOccurringSessions = [BookingStatus.CONFIRMED, BookingStatus.PROCESSING];

export const PaymentStatusSchema = z.object({
  code: z.string(),
  message: z.string(),
});

export const PaymentMethodDetailSchema = z.object({
  fixedChargeId: z.string().optional(),
  billingChargeId: z.union([z.string(), z.number()]).optional(),
  commentId: z.string().optional(),
  transactionCode: z
    .object({
      code: z.string(),
      description: z.string(),
    })
    .optional(),
  pgCno: z.string().optional(),
  msgAuthValue: z.string().optional(),
  shopTransactionId: z.string().optional(),
});

export const BookingHistorySchema = z.object({
  id: z.string(),
  createdAt: z.string().datetime(),
  status: z.nativeEnum(BookingHistoryStatus),
  previousParticipantsCount: z.number(),
  newParticipantsCount: z.number(),
  previousPriceTiers: z.array(BookingPriceTierSchema),
  newPriceTiers: z.array(BookingPriceTierSchema),
  previousAmount: z.number(),
  newAmount: z.number(),
  previousExperiencePaidAmount: z.number(),
  newExperiencePaidAmount: z.number(),
  previousSubtotal: z.number(),
  newSubtotal: z.number(),
  previousHostCommission: z.number(),
  newHostCommission: z.number(),
  refundAmount: z.number().nullable(),
  experienceRefundAmount: z.number().nullable(),
  addOns: z.array(DetailedBookingAddOnItemSchema).default([]),
  participants: z.array(BaseParticipantSchema).default([]),
  previousExperienceId: z.string().nullable(),
  newExperienceId: z.string().nullable(),
  previousExperienceEventId: z.string().nullable(),
  newExperienceEventId: z.string().nullable(),
});

export const MetaSchema = z
  .object({
    paymentStatus: PaymentStatusSchema.optional(),
    paymentDetail: PaymentDetailSchema.nullable(),
    paymentMethodDetail: PaymentMethodDetailSchema.nullable(),
  })
  .partial();

/** One day of a continuous booking as it was charged, and whether it still stands. */
export const BookingDaySchema = z.object({
  id: z.string().uuid(),
  bookingHistoryId: z.string().uuid().nullable(),
  date: z.string(),
  sequence: z.number().int(),
  startDateTime: z.string(),
  endDateTime: z.string(),
  unit: z.enum(['daily', 'nightly', 'flexible', 'session']),
  subtotal: z.number(),
  discountAmount: z.number(),
  taxAmount: z.number(),
  total: z.number(),
  refundAmount: z.number().nullable(),
  releasedAmount: z.number().nullable().optional(),
  priceTiers: z.array(BookingDayPriceTierSchema),
  taxes: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
      amount: z.number(),
      includedInPrice: z.boolean(),
      isHostTax: z.boolean(),
    }),
  ),
  cancelledAt: z.string().nullable(),
});
export type BookingDay = z.infer<typeof BookingDaySchema>;

export const BookingDetailSchema = z.object({
  id: z.string(),
  eventId: z.string(),
  amount: z.coerce.number(),
  experienceDiscountAmount: z.number(),
  experiencePaidAmount: z.number(),
  isModified: z.boolean(),
  experienceRefundedAmount: z.number(),
  currency: z.string(),
  amountRefunded: z.coerce.number(),
  date: z.string(),
  confirmationCode: z.string(),
  status: z.nativeEnum(BookingStatus),
  sessionDate: z.string(),
  sessionDuration: z.number(),
  isCancelled: z.boolean(),
  isPartiallyCancelled: z.boolean(),
  createdAt: z.string().datetime(),
  experience: z.object({
    id: z.string(),
    title: z.string(),
    hostName: z.string(),
    location: LocationSchema,
    number: z.string().nullable(),
    resourceGroupId: z.string().nullable().optional(),
  }),
  customer: z.object({
    id: z.string(),
    name: z.string(),
    emailAddress: z.string(),
  }),
  purchaser: z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    emailAddress: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    customQuestionAnswers: z.array(CustomQuestionAnswerBookingResponseSchema).optional(),
  }),
  participants: z.array(
    BaseParticipantSchema.extend({
      customQuestionAnswers: z.array(CustomQuestionAnswerBookingResponseSchema).optional(),
      cancelledAt: z.string().datetime().nullable(),
    }),
  ),
  earning: z.coerce.number().nullable(),
  hostCommissionType: z.nativeEnum(HostCommissionType),
  paymentMethod: z.nativeEnum(PaymentMethods),
  isVoided: z.boolean(),
  priceTiers: z.array(BookingPriceTierSchema),
  isFullyRefunded: z.boolean(),
  isPartiallyRefunded: z.boolean(),
  refundHistory: z.array(RefundHistorySchema).nullable(),
  notes: z.string().nullable(),
  mode: z.nativeEnum(BookingMode),
  experienceCoverPicture: IMediaReferenceSchema.nullable(),
  chargeId: z.string().nullable(),
  cancellationPolicyId: z.string().nullable(),
  reschedulePolicyId: z.string().nullable(),
  experienceSettings: ExperienceSettingsSchema,
  meta: MetaSchema.nullable(),
  brand: z.object({
    id: z.string(),
    name: z.string(),
  }),
  internalNotes: z.string().nullable(),
  integrations: z.object({
    alice: z.object({
      ticketIds: z.array(z.coerce.number()),
    }),
  }),
  isDisputed: z.boolean(),
  disputedReason: z.string().nullable(),
  cartId: z.string().uuid().nullable(),
  cartConfirmationCode: z.string().nullable(),
  addOns: z.array(DetailedBookingAddOnItemSchema).nullable(),
  listingId: z.string().nullable(),
  cardBrand: z.string().nullable().optional(),
  cardLast4: z.string().nullable().optional(),
  resourceGroupCollectionId: z.string().uuid().nullable(),
  bookingHistories: z.array(BookingHistorySchema).nullable(),
  couponId: z.string().uuid().nullable(),
  promoterBookingDetails: z
    .object({
      bookedOnBrandId: z.string().uuid(),
      bookedOnBrandName: z.string().nullable(),
    })
    .nullable(),
  creditCodeAmount: z.number().nullable().optional(),
  hasProcessableDueTransactions: z.literal(true).optional(),
  appliedCreditCodes: z
    .array(
      z.object({
        code: z.string(),
        amount: z.number(),
      }),
    )
    .nullable()
    .optional(),
  bookingDays: z.array(BookingDaySchema).optional(),
});

export const BookingDetailResponseSchema = paginateResponseSchema(BookingDetailSchema);

export type BookingDetail = z.infer<typeof BookingDetailSchema>;
export type BookingDetailResponse = z.infer<typeof BookingDetailResponseSchema>;

export enum BookingStatusFilterValues {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded',
  PARTIALLY_REFUNDED = 'partially_refunded',
  VOIDED = 'voided',
}

export const GetPaginatedBookingsQuerySchema = paginateQuerySchema({
  sortBy: ['purchaser', 'experience', 'dateBooked', 'amount', 'status', 'brand', 'sessionDate'] as const,
}).extend({
  organizationId: z.string().uuid().optional(),
  experienceId: z.string().uuid().optional(),
  resourceGroupCollectionId: z.string().uuid().optional(),
  excludeFailed: BooleanStringSchema.optional(),
  excludeCancelled: BooleanStringSchema.optional(),
  promoted: BooleanStringSchema.optional(),
  cartId: z.string().uuid().optional(),
  cartConfirmationCode: z.string().optional(),
  confirmationCode: z.string().optional(),
  createdFrom: DateStringSchema.optional(),
  createdTo: DateStringSchema.optional(),
  updatedFrom: DateStringSchema.optional(),
  updatedTo: DateStringSchema.optional(),
  bookingIds: z.array(z.string().uuid()).min(1).optional(),
  purchaserLastName: z.string().optional(),
  purchaserEmail: z.string().optional(),
  methodNumber: z.string().optional(),
  loyaltyProgramId: z.string().optional(),
  memberId: z.string().optional(),
  filters: z
    .array(
      z.union([
        z.object({
          operator: z.literal('range'),
          name: z.literal('transactionDateRange'),
          value: z.object({ from: DateStringSchema, to: DateStringSchema }),
        }),
        z.object({
          operator: z.literal('range'),
          name: z.literal('experienceDateRange'),
          value: z.object({ from: DateStringSchema, to: DateStringSchema }),
        }),
        z.object({
          operator: z.enum(['equals', 'greater_than', 'less_than']),
          name: z.literal('amount'),
          value: z.coerce.number().pipe(z.coerce.string()),
        }),
        z.object({
          operator: z.literal('range'),
          name: z.literal('amount'),
          value: z.object({
            from: z.coerce.number().pipe(z.coerce.string()),
            to: z.coerce.number().pipe(z.coerce.string()),
          }),
        }),
        z.object({
          operator: z.literal('in'),
          name: z.literal('hosts'),
          value: z.array(z.string().uuid()),
        }),
        z.object({
          operator: z.literal('in'),
          name: z.literal('status'),
          value: z.array(z.nativeEnum(BookingStatusFilterValues)),
        }),
        z.object({
          operator: z.literal('in'),
          name: z.literal('paymentMethod'),
          value: z.array(z.nativeEnum(PaymentMethods)),
        }),
        z.object({
          operator: z.literal('in'),
          name: z.literal('products'),
          value: z.array(z.nativeEnum(ProductLine)),
        }),
        z.object({
          name: z.literal('disputed'),
          operator: z.literal('equals'),
          value: z.enum(['true', 'false']),
        }),
      ]),
    )
    .optional(),
});

export const GetPaginatedBookingsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ query }) => `v1/bookings?${buildQueryString(query)}`,
  responseSchema: BookingDetailResponseSchema,
  requestSchema: z.void(),
  querySchema: GetPaginatedBookingsQuerySchema,
});

export const GetPaginatedBrandBookingsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, query }: { brandId: string; query: z.input<typeof GetPaginatedBookingsQuerySchema> }) =>
    `v1/brands/${brandId}/bookings?${buildQueryString(query)}`,
  responseSchema: BookingDetailResponseSchema,
  requestSchema: z.void(),
  querySchema: GetPaginatedBookingsQuerySchema,
});

export const PaymentResultSchema = z.object({
  amount: z.coerce.number(),
  currency: z.string(),
  confirmationCode: z.string(),
  subjectId: z.string(),
  subjectType: z.string(),
  clientSecret: z.string().nullable().optional(),
  metadata: z
    .record(z.unknown())
    .nullable()
    .optional()
    .transform((metadata) => metadata ?? null),
});

export const PublicBookingResponseSchema = BookingDetailSchema.pick({
  id: true,
  confirmationCode: true,
  cartId: true,
  cartConfirmationCode: true,
  amount: true,
  currency: true,
  amountRefunded: true,
  status: true,
  purchaser: true,
  participants: true,
  paymentMethod: true,
  createdAt: true,
  notes: true,
  internalNotes: true,
}).extend({
  event: z.object({
    timezone: z.string(),
    startDateTime: LocalDateTimeStringSchema,
  }),
  experience: z.object({
    id: z.string().uuid(),
    title: z.string(),
  }),
});

const PublicCancelBookingRequestItemSchema = z.object({
  bookingId: z.string().uuid(),
  cancel: z.literal(true).optional(),
  participants: z.array(z.string().uuid()).min(1).optional(),
  refundAmount: z.number().optional(),
});

export const PublicCancelBookingsRequestSchema = z.object({
  bookings: z.array(PublicCancelBookingRequestItemSchema),
});

export const PublicCancelBookingEndpoint = makeEndpoint({
  method: 'POST',
  url: () => `v1/bookings/cancel`,
  responseSchema: z.object({ items: z.array(PublicBookingResponseSchema) }),
  requestSchema: PublicCancelBookingsRequestSchema,
});

export enum RefundStatus {
  SUCCEEDED = 'succeeded',
  PARTIALLY_SUCCEEDED = 'partially_succeeded',
}

export const BookingAddOnItemRefundSchema = z.object({
  id: z.string().uuid(),
  refundAmount: z.number().nonnegative(),
});

export const RefundBookingRequestSchema = z.object({
  bookingHistoryId: z.string().uuid(),
  experienceRefundAmount: z.number().nonnegative(),
  bookingAddOnItems: z.array(BookingAddOnItemRefundSchema).optional(),
  bookingDayIds: z.array(z.string().uuid()).optional(),
});

export const RefundBookingResponseSchema = z.object({
  status: z.nativeEnum(RefundStatus),
  refundedAmount: z.number().nonnegative(),
  failedRefunds: z.record(
    z.string().uuid(),
    z.object({
      refundAmount: z.number(),
      error: z.string(),
    }),
  ),
});

export enum BookingStateType {
  CREATED = 'created',
  UPDATED = 'updated',
  RESCHEDULED = 'rescheduled',
}

export const RefundBookingEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ bookingId }: { bookingId: string }) => `v1/bookings/${bookingId}/refund`,
  responseSchema: RefundBookingResponseSchema,
  requestSchema: z.object({
    refunds: z.array(RefundBookingRequestSchema),
  }),
});

const CancelBookingRequestSchema = z.object({
  participantIds: z.array(z.object({ id: z.string().uuid() })),
  bookedAddOnIds: z.array(z.object({ id: z.string().uuid() })),
  bookingDayIds: z.array(z.object({ id: z.string().uuid() })).optional(),
  shortenTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/)
    .optional(),
});

export const CancelBookingEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ bookingId }: { bookingId: string }) => `v1/bookings/${bookingId}/cancel`,
  responseSchema: z.void(),
  requestSchema: CancelBookingRequestSchema,
});

export const ProcessBookingScheduledTransactionsEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ bookingId }: { bookingId: string }) => `v1/bookings/${bookingId}/process-scheduled-transactions`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});

const AddToBookingRequestSchema = z.object({
  participants: z
    .array(
      z.object({
        priceTierName: z.string(),
        quantity: z.number().positive({ message: 'Participant quantity must be greater than 0' }),
      }),
    )
    .default([]),
  addOns: z.array(BookingListingAddOnItemSchema).default([]),
  overrideSessionAddOnLimits: z.boolean().optional(),
});

const AddToBookingResponseSchema = z.object({
  bookingHistoryId: z.string().uuid(),
});

export type AddToBookingRequest = z.infer<typeof AddToBookingRequestSchema>;

export const AddToBookingEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ bookingId }: { bookingId: string }) => `v1/bookings/${bookingId}/add-to-booking`,
  responseSchema: AddToBookingResponseSchema,
  requestSchema: AddToBookingRequestSchema,
});

const GetPromoterBookingIdsResponseSchema = z.array(z.string().uuid());

export const GetPromoterBookingIdsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/promoted-booking-ids`,
  responseSchema: GetPromoterBookingIdsResponseSchema,
  requestSchema: z.void(),
});

export const BookingsByCartIdSchema = z.object({
  id: z.string(),
  eventId: z.string(),
  amount: z.coerce.number(),
  experiencePaidAmount: z.number(),
  experienceRefundedAmount: z.number(),
  currency: z.string(),
  amountRefunded: z.coerce.number(),
  date: z.string(),
  confirmationCode: z.string(),
  status: z.nativeEnum(BookingStatus),
  sessionDate: z.string(),
  sessionDuration: z.number(),
  isCancelled: z.boolean(),
  isPartiallyCancelled: z.boolean(),
  cancellationPolicyId: z.string().nullable(),
  reschedulePolicyId: z.string().nullable(),
  createdAt: z.string().datetime(),
  experience: z.object({
    id: z.string(),
    title: z.string(),
    hostName: z.string(),
    location: LocationSchema,
    number: z.string().nullable(),
    resourceGroupId: z.string().nullable().optional(),
  }),
  customer: z.object({
    id: z.string(),
    name: z.string(),
    emailAddress: z.string(),
  }),
  purchaser: z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    emailAddress: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    customQuestionAnswers: z.array(CustomQuestionAnswerBookingResponseSchema).optional(),
  }),
  participants: z.array(
    BaseParticipantSchema.extend({
      customQuestionAnswers: z.array(CustomQuestionAnswerBookingResponseSchema).optional(),
      cancelledAt: z.string().datetime().nullable(),
      priceTierPrice: z.number().nullable().optional(),
      priceTierId: z.string().nullable().optional(),
    }),
  ),
  earning: z.coerce.number().nullable(),
  hostCommissionType: z.nativeEnum(HostCommissionType),
  paymentMethod: z.nativeEnum(PaymentMethods),
  isVoided: z.boolean(),
  priceTiers: z.array(BookingPriceTierSchema),
  isFullyRefunded: z.boolean(),
  isPartiallyRefunded: z.boolean(),
  refundHistory: z.array(RefundHistorySchema).nullable(),
  notes: z.string().nullable(),
  experienceCoverPicture: IMediaReferenceSchema.nullable(),
  chargeId: z.string().nullable(),
  experienceSettings: ExperienceSettingsSchema,
  meta: MetaSchema.nullable(),
  brand: z.object({
    id: z.string(),
    name: z.string(),
  }),
  internalNotes: z.string().nullable(),
  integrations: z.object({
    alice: z.object({
      ticketIds: z.array(z.coerce.number()),
    }),
  }),
  isDisputed: z.boolean(),
  disputedReason: z.string().nullable(),
  cartId: z.string().uuid().nullable(),
  cartConfirmationCode: z.string().nullable(),
  addOns: z.array(DetailedBookingAddOnItemSchema).nullable(),
  listingId: z.string().nullable(),
  appliedCreditCodes: z
    .array(
      z.object({
        code: z.string(),
        appliedValue: z.number(),
        sequence: z.number(),
      }),
    )
    .nullable(),
  taxes: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        amount: z.number(),
        includedInPrice: z.boolean(),
        isHostTax: z.boolean().optional(),
      }),
    )
    .nullable(),
});
